import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProducts } from '../contexts/ProductContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Product, ProductVariant, getDisplayCategory } from '../types/product';
import { extractVariantInfo, getVariantDisplayName } from '../utils/variantUtils';
import RibbonSelector from '../components/product/RibbonSelector';
import ProductCustomization from '../components/product/ProductCustomization';
import { parseCustomizationFields, CustomizationData } from '../types/customization';
import { toast } from 'react-toastify';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getProductById, productVariants, trophiesVariants, getRibbons } = useProducts();
  const { addItem, totalItems } = useCart();
  const { currentUser } = useAuth();

  const [product, setProduct] = useState<Product | null>(null);
  const [productVariant, setProductVariant] = useState<ProductVariant | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [ribbons, setRibbons] = useState<Product[]>([]);
  const [selectedRibbon, setSelectedRibbon] = useState<Product | null>(null);
  const [customizationData, setCustomizationData] = useState<CustomizationData>({});

  useEffect(() => {
    const fetchedProduct = getProductById(id!);
    if (fetchedProduct) {
      setProduct(fetchedProduct);

      // Debug: Log product customization info
      console.log('📝 Product loaded:', fetchedProduct.name);
      console.log('📝 Customizable:', fetchedProduct.customizable);
      console.log('📝 Customization Fields:', fetchedProduct.customizationFields);

      // Find the ProductVariant group this product belongs to
      const { baseId } = extractVariantInfo(fetchedProduct.productId);
      const variantGroup = trophiesVariants.find(v => v.baseId === baseId);

      if (variantGroup) {
        setProductVariant(variantGroup);

        // Select the current product as the selected variant
        setSelectedVariant(fetchedProduct);
      } else {
        // Product has no variants
        setSelectedVariant(fetchedProduct);
      }

      // Load ribbons if this is a medal
      const displayCategory = getDisplayCategory(fetchedProduct);
      if (displayCategory.toLowerCase() === 'medal') {
        const availableRibbons = getRibbons();
        setRibbons(availableRibbons);
        console.log(`🎀 Loaded ${availableRibbons.length} ribbons for medal`);
      } else {
        setRibbons([]);
        setSelectedRibbon(null);
        console.log(`🎀 Not a medal (displayCategory: "${displayCategory}") - skipping ribbon load`);
      }

      // Parse customization fields for debugging
      if (fetchedProduct.customizable && fetchedProduct.customizationFields) {
        const fields = parseCustomizationFields(fetchedProduct.customizationFields);
        console.log(`✏️ Parsed ${fields.length} customization fields:`, fields);
      }
    }
  }, [id, getProductById, trophiesVariants, getRibbons]);

  if (!product) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontSize: '16px',
        color: '#6B7280'
      }}>
        Product not found
      </div>
    );
  }

  const hasVariants = productVariant ? productVariant.hasMultipleVariants : false;
  const displayProduct = selectedVariant || product;
  const ribbonPrice = selectedRibbon?.price || 0;
  const itemPrice = (displayProduct.price || 0) + ribbonPrice;
  const totalPrice = itemPrice * quantity;

  const handleVariantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const variantId = e.target.value;
    if (productVariant) {
      const variant = productVariant.variants.find(v => v.id === variantId);
      if (variant) {
        setSelectedVariant(variant);
        // Update URL to reflect the new variant
        navigate(`/product/${variant.id}`, { replace: true });
      }
    }
    setQuantity(1); // Reset quantity when variant changes
  };

  const handleQuantityDecrease = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleQuantityIncrease = () => {
    setQuantity(quantity + 1);
  };

  const handleAddToCart = async () => {
    // Guests can add items to cart - login only required at checkout
    if (hasVariants && !selectedVariant) {
      toast.error('Please select a variant', {
        position: 'top-center',
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      const productToAdd = selectedVariant || product;

      // Add with customization data if present
      const hasCustomization = Object.keys(customizationData).length > 0;
      console.log('🛒 Adding to cart with customization:', hasCustomization, customizationData);

      addItem(
        productToAdd,
        quantity,
        hasCustomization ? customizationData : undefined,
        selectedRibbon || undefined
      );

      const message = selectedRibbon
        ? `Added ${quantity} × ${productToAdd.name} + ${selectedRibbon.name} to cart`
        : hasCustomization
        ? `Added ${quantity} × ${productToAdd.name} with personalization to cart`
        : 'Added to cart';

      toast.success(message, {
        position: 'top-center',
        autoClose: 3000,
        onClick: () => navigate('/cart'),
        style: { cursor: 'pointer' }
      });

      setQuantity(1); // Reset quantity
      setSelectedRibbon(null); // Clear ribbon selection
      setCustomizationData({}); // Clear customization
    } catch (error) {
      toast.error('Failed to add to cart', {
        position: 'top-center',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/home');
  };

  const handleCartClick = () => {
    navigate('/cart');
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#F9FAFB',
      paddingBottom: '80px' // Space for bottom nav if present
    }}>
      {/* App Bar */}
      <div style={{
        backgroundColor: '#FFFFFF',
        height: '56px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        {/* Back Button */}
        <button
          onClick={handleBack}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            color: '#1A1A1A'
          }}
        >
          <img
            src="/assets/images/arrow.png"
            alt="Back"
            style={{
              width: '24px',
              height: '24px',
              transform: 'rotate(90deg)'
            }}
          />
        </button>

        {/* Title */}
        <h1 style={{
          fontSize: '18px',
          fontWeight: 600,
          color: '#1A1A1A',
          margin: 0,
          flex: 1,
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: '0 16px'
        }}>
          {product.name}
        </h1>

        {/* Cart Icon with Badge */}
        <button
          onClick={handleCartClick}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            color: '#1A1A1A'
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
          {totalItems > 0 && (
            <span style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              backgroundColor: '#B8860B',
              color: 'white',
              fontSize: '10px',
              fontWeight: 'bold',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {totalItems}
            </span>
          )}
        </button>
      </div>

      {/* Product Image Carousel */}
      <div style={{
        height: '300px',
        backgroundColor: '#F9FAFB',
        position: 'relative'
      }}>
        {displayProduct.images && displayProduct.images.length > 0 ? (
          displayProduct.images.length > 1 ? (
            <Swiper
              modules={[Pagination]}
              pagination={{
                clickable: true,
                bulletClass: 'swiper-pagination-bullet-custom',
                bulletActiveClass: 'swiper-pagination-bullet-active-custom'
              }}
              style={{ height: '100%' }}
            >
              {displayProduct.images.map((image, index) => (
                <SwiperSlide key={index}>
                  <div style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img
                      src={image}
                      alt={`${displayProduct.name} - ${index + 1}`}
                      style={{
                        maxHeight: '100%',
                        maxWidth: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={displayProduct.images[0]}
                alt={displayProduct.name}
                style={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )
        ) : (
          <div style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          </div>
        )}
      </div>

      {/* Product Information Section */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '20px 20px 0 0',
        padding: '20px',
        marginTop: '-20px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Category & SKU Row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <span style={{
            backgroundColor: '#E3F2FD',
            color: '#1976D2',
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
            padding: '8px 12px',
            borderRadius: '20px'
          }}>
            {displayProduct.category || 'Trophy'}
          </span>
          <span style={{
            fontSize: '12px',
            color: '#6B7280'
          }}>
            SKU: {displayProduct.sku || displayProduct.productId}
          </span>
        </div>

        {/* Product Name */}
        <h2 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#1A1A1A',
          marginBottom: '16px',
          lineHeight: '1.3'
        }}>
          {displayProduct.name}
        </h2>

        {/* Variant Selector */}
        {hasVariants && (
          <div style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '16px'
          }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: '#1A1A1A',
              fontFamily: 'Montserrat, sans-serif',
              marginBottom: '8px'
            }}>
              Select Variant:
            </label>
            <select
              value={selectedVariant?.id || ''}
              onChange={handleVariantChange}
              style={{
                width: '100%',
                backgroundColor: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '12px',
                paddingRight: '40px',
                fontSize: '14px',
                fontFamily: 'Montserrat, sans-serif',
                color: '#1A1A1A',
                cursor: 'pointer',
                outline: 'none',
                appearance: 'none',
                backgroundImage: 'url(/assets/images/arrow.png)',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                backgroundSize: '16px 16px'
              }}
            >
              <option value="">Choose a variant</option>
              {productVariant && productVariant.variants.map((variant) => {
                const { variantSuffix } = extractVariantInfo(variant.productId);
                const variantName = getVariantDisplayName(variantSuffix);
                const label = variantName
                  ? `${variantName} (${variant.productId})${variant.size ? ` - ${variant.size}` : ''}`
                  : `${variant.productId}${variant.size ? ` - ${variant.size}` : ''}`;

                return (
                  <option key={variant.id} value={variant.id}>
                    {label}
                    {variant.price !== null && ` - €${variant.price.toFixed(2)}`}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        {/* Ribbon Selector (only for medals) */}
        <RibbonSelector
          ribbons={ribbons}
          selectedRibbon={selectedRibbon}
          onSelectRibbon={setSelectedRibbon}
        />

        {/* Price & Stock Row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            {displayProduct.price !== null ? (
              <div>
                <div style={{
                  fontSize: '28px',
                  fontWeight: 'bold',
                  color: '#10B981'
                }}>
                  €{displayProduct.price.toFixed(2)}
                  {selectedRibbon && (
                    <span style={{ fontSize: '20px', color: '#6B7280' }}>
                      {' '}+ €{ribbonPrice.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <span style={{ fontSize: '18px', color: '#6B7280' }}>Price TBA</span>
            )}
          </div>
          <div style={{
            backgroundColor: displayProduct.inStock ? '#E8F5E9' : '#FFEBEE',
            color: displayProduct.inStock ? '#2E7D32' : '#C62828',
            padding: '8px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            {displayProduct.inStock ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
                In Stock
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
                </svg>
                Out of Stock
              </>
            )}
          </div>
        </div>

        {/* Size Information */}
        {displayProduct.size && displayProduct.size.trim() !== '' && (
          <div style={{ marginBottom: '16px' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#1A1A1A',
              marginRight: '8px'
            }}>
              Size:
            </span>
            <span style={{
              fontSize: '14px',
              color: '#6B7280'
            }}>
              {displayProduct.size}
            </span>
          </div>
        )}

        {/* Description Section */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#1A1A1A',
            marginBottom: '8px'
          }}>
            Description
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            lineHeight: '1.5',
            margin: 0
          }}>
            {displayProduct.description}
          </p>
        </div>

        {/* Product Customization (only for customizable products) */}
        {displayProduct.customizable && displayProduct.customizationFields && (
          <ProductCustomization
            fields={parseCustomizationFields(displayProduct.customizationFields)}
            onCustomizationChange={setCustomizationData}
          />
        )}

        {/* Quantity Selector */}
        <div style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Quantity Controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <button
              onClick={handleQuantityDecrease}
              disabled={quantity === 1}
              style={{
                background: 'none',
                border: 'none',
                cursor: quantity === 1 ? 'not-allowed' : 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                color: quantity === 1 ? '#9CA3AF' : '#B8860B'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z" />
              </svg>
            </button>

            <div style={{
              width: '80px',
              backgroundColor: '#F3F4F6',
              borderRadius: '8px',
              padding: '8px',
              textAlign: 'center',
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#1A1A1A'
            }}>
              {quantity}
            </div>

            <button
              onClick={handleQuantityIncrease}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                color: '#B8860B'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
              </svg>
            </button>
          </div>

          {/* Total Price */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end'
          }}>
            <span style={{
              fontSize: '14px',
              color: '#6B7280',
              marginBottom: '4px'
            }}>
              Total:
            </span>
            <span style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#10B981'
            }}>
              €{totalPrice.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          disabled={loading || !displayProduct.inStock || (hasVariants && !selectedVariant)}
          style={{
            width: '100%',
            backgroundColor: loading || !displayProduct.inStock || (hasVariants && !selectedVariant) ? '#9CA3AF' : '#B8860B',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 'bold',
            cursor: loading || !displayProduct.inStock || (hasVariants && !selectedVariant) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'background-color 0.2s'
          }}
        >
          {loading ? (
            'Adding...'
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              Add to Cart (€{totalPrice.toFixed(2)})
            </>
          )}
        </button>
      </div>

      {/* Custom Swiper Pagination Styles */}
      <style>{`
        .swiper-pagination-bullet-custom {
          width: 6px;
          height: 6px;
          background-color: #E5E7EB;
          opacity: 1;
          border-radius: 50%;
          margin: 0 4px !important;
          transition: all 0.3s;
        }
        .swiper-pagination-bullet-active-custom {
          width: 8px;
          height: 8px;
          background-color: #B8860B;
        }
        .swiper-pagination {
          bottom: 16px !important;
        }
      `}</style>
    </div>
  );
};

export default ProductDetailPage;
